import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { useModalAlert } from './ModalAlert';
import {
  FaEdit,
  FaSave,
  FaTimes,
  FaSpinner,
  FaExclamationTriangle,
  FaPlus,
  FaTrash,
  FaCheck,
} from 'react-icons/fa';
import { TabsExtended } from './TabsExtended';
import 'highlight.js/styles/github-dark.css';

const PortfolioPromptEditor = ({
  agentId,
  config,
  authToken,
  onPromptChange,
  className = ""
}) => {
  const { showSuccess, showError, showWarning } = useModalAlert();
  const apiUrl = config?.apiBaseUrl || window.location.origin.replace('/web', '');

  // Fetch personas for the "load persona" dropdown
  const [personas, setPersonas] = useState([]);
  useEffect(() => {
    if (!authToken) return;
    fetch(`${apiUrl}/agents/prompts/personas`, {
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then(r => r.json())
      .then(setPersonas)
      .catch(() => {});
  }, [apiUrl, authToken]);

  // All prompts for this agent
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Currently editing prompt (null = list view)
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const editorRef = useRef(null);
  const previewRef = useRef(null);

  // Fetch all prompts
  const fetchPrompts = useCallback(async () => {
    if (!agentId || !authToken) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiUrl}/agents/${agentId}/prompts`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error(`Failed to fetch prompts: ${res.statusText}`);
      const data = await res.json();
      setPrompts(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [agentId, apiUrl, authToken]);

  useEffect(() => { fetchPrompts(); }, [fetchPrompts]);

  // Start editing an existing prompt
  const handleEdit = (prompt) => {
    setEditingPrompt({ ...prompt });
    setIsNew(false);
  };

  // Start creating a new prompt
  const handleCreate = () => {
    setEditingPrompt({
      name: '',
      prompt_text: '',
      version_tag: 'v1.0',
      is_active: false,
    });
    setIsNew(true);
  };

  // Cancel editing
  const handleCancel = () => {
    setEditingPrompt(null);
    setIsNew(false);
  };

  // Save (create or update)
  const handleSave = async () => {
    if (!editingPrompt || saving) return;
    if (!editingPrompt.name.trim()) {
      setError('Prompt name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      };

      let res;
      if (isNew) {
        res = await fetch(`${apiUrl}/agents/${agentId}/prompts`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            name: editingPrompt.name,
            prompt_text: editingPrompt.prompt_text,
            version_tag: 'v1.0',
            is_active: editingPrompt.is_active,
          }),
        });
      } else {
        res = await fetch(`${apiUrl}/agents/${agentId}/prompts/${editingPrompt.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            name: editingPrompt.name,
            prompt_text: editingPrompt.prompt_text,
            version_tag: 'v1.0',
            is_active: editingPrompt.is_active,
          }),
        });
      }

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Save failed (${res.status})`);
      }

      showSuccess('Saved', `Prompt "${editingPrompt.name}" saved.`, { autoClose: true });
      setEditingPrompt(null);
      setIsNew(false);
      fetchPrompts();
      if (onPromptChange) onPromptChange();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Activate a prompt
  const handleActivate = async (promptId) => {
    try {
      const res = await fetch(`${apiUrl}/agents/${agentId}/prompts/${promptId}/activate`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error(`Activate failed (${res.status})`);
      fetchPrompts();
      if (onPromptChange) onPromptChange();
    } catch (err) {
      showError('Activate Failed', err.message);
    }
  };

  // Delete a prompt
  const handleDelete = (prompt) => {
    showWarning(
      'Delete Prompt',
      `Delete "${prompt.name}"? This cannot be undone.`,
      {
        showCancel: true,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        onConfirm: async () => {
          try {
            const res = await fetch(`${apiUrl}/agents/${agentId}/prompts/${prompt.id}`, {
              method: 'DELETE',
              headers: { Authorization: `Bearer ${authToken}` },
            });
            if (!res.ok) {
              const data = await res.json().catch(() => ({}));
              throw new Error(data.detail || `Delete failed (${res.status})`);
            }
            showSuccess('Deleted', `Prompt "${prompt.name}" deleted.`, { autoClose: true });
            fetchPrompts();
          } catch (err) {
            showError('Delete Failed', err.message);
          }
        },
      }
    );
  };

  // Scroll sync
  const handleEditorScroll = () => {
    if (previewRef.current && editorRef.current) {
      const pct = editorRef.current.scrollTop /
        (editorRef.current.scrollHeight - editorRef.current.clientHeight);
      previewRef.current.scrollTop = pct *
        (previewRef.current.scrollHeight - previewRef.current.clientHeight);
    }
  };

  if (!agentId) {
    return (
      <div className={`p-4 text-center text-sm text-slate-500 italic ${className}`}>
        Assign an agent to manage prompts.
      </div>
    );
  }

  if (loading && prompts.length === 0) {
    return (
      <div className={`flex items-center justify-center p-6 ${className}`}>
        <FaSpinner className="animate-spin text-slate-400 mr-2" />
        <span className="text-slate-400 text-sm">Loading prompts...</span>
      </div>
    );
  }

  // ── Editor View ──
  if (editingPrompt) {
    return (
      <div className={className}>
        {error && (
          <div className="mb-2 px-2 py-1.5 bg-red-900/30 border border-red-700 rounded flex items-center gap-2">
            <FaExclamationTriangle className="text-red-400 flex-shrink-0 text-xs" />
            <span className="text-red-300 text-xs">{error}</span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <button onClick={handleCancel} className="text-xs text-slate-400 hover:text-white">
            ← back to list
          </button>
          <span className="text-xs text-slate-500">{isNew ? 'New prompt' : 'Editing'}</span>
        </div>

        {/* Name input */}
        <div className="mb-2">
          <input
            type="text"
            value={editingPrompt.name}
            onChange={(e) => setEditingPrompt(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Prompt name..."
            className="w-full px-2 py-1.5 border border-slate-600 text-sm rounded bg-slate-800 text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
          />
        </div>

        {/* Editor / Preview tabs */}
        <TabsExtended defaultIndex={0}>
          <div title="Editor">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-1.5 mb-2">
              {personas.length > 0 && (
                <select
                  value=""
                  onChange={(e) => {
                    const persona = personas.find(p => p.id === e.target.value);
                    if (persona) {
                      setEditingPrompt(prev => ({ ...prev, prompt_text: persona.prompt_text }));
                    }
                  }}
                  className="px-2 py-1 text-xs border border-slate-600 rounded bg-slate-800 text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
                >
                  <option value="" disabled>Load persona...</option>
                  {personas.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              )}
              <div className="flex-1" />
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1 px-2.5 py-1 bg-green-700 hover:bg-green-600 text-white rounded text-xs transition-colors"
              >
                {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                Save
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center gap-1 px-2.5 py-1 bg-slate-600 hover:bg-slate-500 text-white rounded text-xs transition-colors"
              >
                <FaTimes /> Cancel
              </button>
            </div>

            <textarea
              ref={editorRef}
              value={editingPrompt.prompt_text}
              onChange={(e) => setEditingPrompt(prev => ({ ...prev, prompt_text: e.target.value }))}
              onScroll={handleEditorScroll}
              rows={18}
              className="w-full px-3 py-2 border border-slate-600 rounded bg-slate-800 text-white focus:outline-none focus:ring-1 focus:ring-cyan-500 resize-none font-mono text-xs leading-relaxed"
              placeholder="Enter your trading strategy and risk rules here..."
            />
          </div>

          <div title="Preview">
            <div
              ref={previewRef}
              className="h-96 overflow-y-auto border border-slate-600 rounded bg-slate-800 p-3"
            >
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeHighlight]}
                  components={{
                    code: ({ node, inline, className, children, ...props }) => {
                      const match = /language-(\w+)/.exec(className || '');
                      return !inline && match ? (
                        <pre className="bg-slate-900 p-2 rounded overflow-x-auto">
                          <code className={className} {...props}>{children}</code>
                        </pre>
                      ) : (
                        <code className="bg-slate-900 px-1 py-0.5 rounded" {...props}>{children}</code>
                      );
                    },
                  }}
                >
                  {editingPrompt.prompt_text || '*No content to preview*'}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        </TabsExtended>
      </div>
    );
  }

  // ── List View ──
  return (
    <div className={className}>
      {error && (
        <div className="mb-2 px-2 py-1.5 bg-red-900/30 border border-red-700 rounded flex items-center gap-2">
          <FaExclamationTriangle className="text-red-400 flex-shrink-0 text-xs" />
          <span className="text-red-300 text-xs">{error}</span>
        </div>
      )}

      {/* Header + New button */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-slate-400">{prompts.length} prompt{prompts.length !== 1 ? 's' : ''}</span>
        <button
          onClick={handleCreate}
          className="flex items-center gap-1 px-2.5 py-1 bg-cyan-700 hover:bg-cyan-600 text-white rounded text-xs transition-colors"
        >
          <FaPlus className="text-[10px]" /> New
        </button>
      </div>

      {/* Prompt list */}
      {prompts.length === 0 ? (
        <div className="py-6 text-center">
          <p className="text-slate-500 text-sm mb-2">No prompts yet.</p>
          <button
            onClick={handleCreate}
            className="text-cyan-400 hover:text-cyan-300 text-sm"
          >
            Create your first prompt
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {prompts.map(p => (
            <div
              key={p.id}
              className={`border rounded-lg p-3 transition-colors ${
                p.is_active
                  ? 'border-cyan-700/60 bg-cyan-950/20'
                  : 'border-slate-700 bg-slate-800/30 hover:border-slate-600'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-sm font-medium text-white truncate">{p.name}</span>
                  {p.is_active && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-green-900/40 text-green-400 rounded flex-shrink-0">
                      Active
                    </span>
                  )}
                </div>
                <span className="text-[10px] text-slate-500 flex-shrink-0 ml-2">
                  {p.updated_at
                    ? new Date(p.updated_at).toLocaleDateString()
                    : new Date(p.created_at).toLocaleDateString()}
                </span>
              </div>

              <p className="text-xs text-slate-400 line-clamp-2 mb-2">
                {p.prompt_text?.slice(0, 120) || 'Empty prompt'}{p.prompt_text?.length > 120 ? '...' : ''}
              </p>

              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => handleEdit(p)}
                  className="flex items-center gap-1 px-2 py-0.5 text-[11px] bg-slate-700 hover:bg-slate-600 text-slate-200 rounded transition-colors"
                >
                  <FaEdit className="text-[9px]" /> Edit
                </button>
                {!p.is_active && (
                  <button
                    onClick={() => handleActivate(p.id)}
                    className="flex items-center gap-1 px-2 py-0.5 text-[11px] bg-cyan-800 hover:bg-cyan-700 text-cyan-200 rounded transition-colors"
                  >
                    <FaCheck className="text-[9px]" /> Activate
                  </button>
                )}
                {!p.is_active && (
                  <button
                    onClick={() => handleDelete(p)}
                    className="flex items-center gap-1 px-2 py-0.5 text-[11px] bg-red-900/40 hover:bg-red-900/60 text-red-400 rounded transition-colors ml-auto"
                  >
                    <FaTrash className="text-[9px]" /> Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PortfolioPromptEditor;
